<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useMessageContext } from '../provider.js';
import {
  VOICE_CALL_STATUS,
  VOICE_CALL_DIRECTION,
  VOICE_CALL_OUTBOUND_INIT_STATUS,
  VOICE_CALL_END_REASON,
  MESSAGE_TYPES,
  ATTACHMENT_TYPES,
} from '../constants';
import { useCallActions } from 'dashboard/composables/useCallSession';
import { useWhatsappCallSession } from 'dashboard/composables/useWhatsappCallSession';
import { useCallsStore } from 'dashboard/stores/calls';
import { useAlert } from 'dashboard/composables';
import FreeswitchVoiceClient from 'dashboard/api/channel/voice/freeswitchVoiceClient';
import {
  getVoiceCallProvider,
  VOICE_CALL_PROVIDERS,
} from 'dashboard/helper/inbox';
import { formatDuration } from 'shared/helpers/timeHelper';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import AudioChip from 'next/message/chips/Audio.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const LABEL_MAP = {
  [VOICE_CALL_STATUS.IN_PROGRESS]: 'CONVERSATION.VOICE_CALL.CALL_IN_PROGRESS',
  [VOICE_CALL_STATUS.COMPLETED]: 'CONVERSATION.VOICE_CALL.CALL_ENDED',
};

const ICON_MAP = {
  [VOICE_CALL_STATUS.IN_PROGRESS]: 'i-ph-phone-call-bold',
  [VOICE_CALL_STATUS.COMPLETED]: 'i-ph-phone-bold',
  [VOICE_CALL_STATUS.NO_ANSWER]: 'i-ph-phone-x-bold',
  [VOICE_CALL_STATUS.FAILED]: 'i-ph-phone-x-bold',
  [VOICE_CALL_STATUS.REJECTED]: 'i-ph-phone-x-bold',
};

const { t } = useI18n();
const store = useStore();
const {
  call,
  attachments,
  contentAttributes,
  conversationId,
  currentUserId,
  inboxId,
  sender,
  messageType,
} = useMessageContext();
const { joinCall, endCall, activeCall, hasActiveCall, isJoining } =
  useCallActions();
const whatsappCallSession = useWhatsappCallSession();
const callsStore = useCallsStore();
const contactsUiFlags = useMapGetter('contacts/getUIFlags');
const isInitiatingCall = computed(
  () => contactsUiFlags.value?.isInitiatingCall || false
);

const rawStatus = computed(() => {
  const s =
    call.value?.status ||
    contentAttributes?.value?.data?.status ||
    contentAttributes?.value?.status;
  if (!s) return null;
  return s.toString().replace(/_/g, '-');
});

const status = computed(() => rawStatus.value);

// Server-side call records use `outgoing`/`incoming`, while the Pinia store
// and a few API hops normalise to `outbound`/`inbound`. Accept either so the
// bubble label matches the message orientation no matter the source.
const isOutbound = computed(() => {
  const dir =
    call.value?.direction ||
    contentAttributes?.value?.data?.call_direction ||
    contentAttributes?.value?.data?.callDirection;
  if (
    dir === VOICE_CALL_DIRECTION.OUTGOING ||
    dir === VOICE_CALL_DIRECTION.OUTBOUND ||
    dir === 'outgoing' ||
    dir === 'outbound'
  )
    return true;
  if (
    dir === VOICE_CALL_DIRECTION.INCOMING ||
    dir === VOICE_CALL_DIRECTION.INBOUND ||
    dir === 'incoming' ||
    dir === 'inbound'
  )
    return false;
  // Fall back to the message orientation: agent-authored messages sit on the
  // right (outbound) and contact-authored ones on the left.
  return messageType.value === MESSAGE_TYPES.OUTGOING;
});

const isWhatsapp = computed(
  () =>
    call.value?.provider === VOICE_CALL_PROVIDERS.WHATSAPP ||
    contentAttributes?.value?.data?.call_source === 'whatsapp'
);

const currentInbox = computed(() =>
  store.getters['inboxes/getInbox']?.(inboxId.value)
);

const isFreeswitch = computed(
  () =>
    call.value?.provider === VOICE_CALL_PROVIDERS.FREESWITCH ||
    call.value?.provider === 'freeswitch' ||
    call.value?.provider === 'webrtc' ||
    contentAttributes?.value?.data?.call_source === 'freeswitch' ||
    contentAttributes?.value?.data?.call_source === 'webrtc' ||
    getVoiceCallProvider(currentInbox.value) === VOICE_CALL_PROVIDERS.FREESWITCH
);

const durationSeconds = computed(() => {
  const fromCall = call.value?.durationSeconds || call.value?.duration_seconds;
  if (fromCall != null) return Number(fromCall);
  const data = contentAttributes?.value?.data;
  const fromData = data?.durationSeconds || data?.duration_seconds;
  if (fromData != null) return Number(fromData);
  return null;
});

const formattedDuration = computed(() => formatDuration(durationSeconds.value));

const isCompleted = computed(() => {
  return (
    rawStatus.value === VOICE_CALL_STATUS.COMPLETED ||
    rawStatus.value === 'completed' ||
    rawStatus.value === 'ended' ||
    (durationSeconds.value != null && durationSeconds.value > 0)
  );
});

const endReason = computed(
  () =>
    call.value?.endReason ||
    call.value?.end_reason ||
    contentAttributes?.value?.data?.end_reason ||
    contentAttributes?.value?.data?.endReason
);

const acceptedByAgentId = computed(
  () =>
    call.value?.acceptedByAgentId ||
    call.value?.accepted_by_agent_id ||
    contentAttributes?.value?.data?.accepted_by?.id ||
    contentAttributes?.value?.data?.acceptedBy?.id
);

const conversationAssignee = computed(() => {
  const conversation = store.getters.getConversationById?.(
    conversationId?.value
  );
  return conversation?.meta?.assignee || null;
});

const displayAgentName = computed(() => {
  if (call.value?.acceptedByAgentName) return call.value.acceptedByAgentName;
  const acceptedName =
    contentAttributes?.value?.data?.accepted_by?.name ||
    contentAttributes?.value?.data?.acceptedBy?.name;
  if (acceptedName) return acceptedName;
  if (acceptedByAgentId.value) {
    const agent = store.getters['agents/getAgentById'](acceptedByAgentId.value);
    if (agent?.available_name) return agent.available_name;
    if (agent?.name) return agent.name;
  }
  return conversationAssignee.value?.name || null;
});

const audioAttachment = computed(() =>
  (attachments?.value || []).find(a => a.fileType === ATTACHMENT_TYPES.AUDIO)
);

// Agent who handled the call (initiator on outbound, answerer on inbound), taken
// strictly from the persisted accept fields — never the conversation's current
// assignee, which would mis-attribute a historical call after a reassignment.
const handlerName = computed(() => {
  if (call.value?.acceptedByAgentName) return call.value.acceptedByAgentName;
  const acceptedName =
    contentAttributes?.value?.data?.accepted_by?.name ||
    contentAttributes?.value?.data?.acceptedBy?.name;
  if (acceptedName) return acceptedName;
  if (!acceptedByAgentId.value) return null;
  const agent = store.getters['agents/getAgentById'](acceptedByAgentId.value);
  return agent?.available_name || agent?.name || null;
});

const handledBy = computed(() =>
  handlerName.value
    ? t('CONVERSATION.VOICE_CALL.HANDLED_BY', { agentName: handlerName.value })
    : null
);

const callSid = computed(
  () =>
    call.value?.providerCallId ||
    call.value?.provider_call_id ||
    contentAttributes?.value?.data?.call_sid ||
    contentAttributes?.value?.data?.callId ||
    contentAttributes?.value?.data?.call_id
);

const isActiveThisCall = computed(
  () =>
    hasActiveCall.value &&
    (activeCall.value?.callSid === callSid.value ||
      (activeCall.value?.conversationId === conversationId.value &&
        (rawStatus.value === VOICE_CALL_STATUS.RINGING ||
          rawStatus.value === 'initiating' ||
          !rawStatus.value)))
);

const effectiveStatus = computed(() => {
  if (isActiveThisCall.value) {
    if (activeCall.value?.state === 'connected' || activeCall.value?.answered) {
      return VOICE_CALL_STATUS.IN_PROGRESS;
    }
    return VOICE_CALL_STATUS.RINGING;
  }
  if (isCompleted.value) return VOICE_CALL_STATUS.COMPLETED;
  return rawStatus.value || VOICE_CALL_STATUS.NO_ANSWER;
});

const isFailed = computed(() => {
  if (isCompleted.value || isActiveThisCall.value) return false;
  return true;
});

const isMissedInbound = computed(() => isFailed.value && !isOutbound.value);

const wasDeclinedByAgent = computed(
  () =>
    isMissedInbound.value &&
    (endReason.value === VOICE_CALL_END_REASON.AGENT_REJECTED ||
      endReason.value === 'agent_rejected' ||
      endReason.value === 'CALL_REJECTED' ||
      endReason.value === 'USER_BUSY')
);

const labelKey = computed(() => {
  if (isCompleted.value) {
    return 'CONVERSATION.VOICE_CALL.CALL_ENDED';
  }
  if (isActiveThisCall.value) {
    if (effectiveStatus.value === VOICE_CALL_STATUS.IN_PROGRESS) {
      return 'CONVERSATION.VOICE_CALL.CALL_IN_PROGRESS';
    }
    return isOutbound.value
      ? 'CONVERSATION.VOICE_CALL.OUTGOING_CALL'
      : 'CONVERSATION.VOICE_CALL.INCOMING_CALL';
  }
  if (isOutbound.value) {
    return 'CONVERSATION.VOICE_CALL.OUTGOING_CALL';
  }
  return 'CONVERSATION.VOICE_CALL.MISSED_CALL';
});

const subtext = computed(() => {
  // 1. Completed: "Handled by {agent} · 0:42" (drops either part when absent).
  if (isCompleted.value) {
    return [handledBy.value, formattedDuration.value]
      .filter(Boolean)
      .join(' · ') || t('CONVERSATION.VOICE_CALL.CALL_ENDED');
  }

  // 2. Actively in progress or ringing right now
  if (isActiveThisCall.value) {
    if (effectiveStatus.value === VOICE_CALL_STATUS.IN_PROGRESS) {
      return handledBy.value || t('CONVERSATION.VOICE_WIDGET.CALL_IN_PROGRESS');
    }
    return isOutbound.value
      ? t('CONVERSATION.VOICE_CALL.CALLING')
      : t('CONVERSATION.VOICE_CALL.NOT_ANSWERED_YET');
  }

  // 3. Ended / Unanswered call
  if (isOutbound.value) {
    return t('CONVERSATION.VOICE_CALL.NO_ANSWER_OUTBOUND_SUBTEXT');
  }
  if (wasDeclinedByAgent.value && displayAgentName.value) {
    return t('CONVERSATION.VOICE_CALL.MISSED_CALL_DECLINED_BY', {
      agentName: displayAgentName.value,
    });
  }
  return t('CONVERSATION.VOICE_CALL.MISSED_CALL_INBOUND_SUBTEXT');
});

const iconName = computed(() => {
  if (isActiveThisCall.value) {
    if (effectiveStatus.value === VOICE_CALL_STATUS.IN_PROGRESS) {
      return 'i-ph-phone-call-bold';
    }
    return isOutbound.value
      ? 'i-ph-phone-outgoing-bold'
      : 'i-ph-phone-incoming-bold';
  }
  if (isCompleted.value) {
    return 'i-ph-phone-bold';
  }
  return 'i-ph-phone-x-bold';
});

// Subtle icon container — matches the design's tonal swatch over the bubble bg.
// Status drives the accent: teal for live/completed, ruby for missed inbound, neutral otherwise.
const iconContainerClass = computed(() => {
  if (isCompleted.value || isActiveThisCall.value) {
    return 'bg-n-teal-3 text-n-teal-11';
  }
  if (isMissedInbound.value) {
    return 'bg-n-ruby-3 text-n-ruby-11';
  }
  return 'bg-n-slate-3 text-n-slate-11';
});

const canJoinCall = computed(() => {
  if (status.value !== VOICE_CALL_STATUS.RINGING) return false;
  if (isOutbound.value) return false;
  if (acceptedByAgentId.value) return false;
  if (!callSid.value || !inboxId.value || !conversationId.value) return false;
  if (hasActiveCall.value && activeCall.value?.callSid === callSid.value)
    return false;
  const assignee = conversationAssignee.value;
  if (assignee?.id && assignee.id !== currentUserId.value) return false;
  return true;
});

const recordingAttachment = computed(() => {
  if (audioAttachment.value) return audioAttachment.value;
  const url = call.value?.recordingUrl;
  if (!url) return null;
  return {
    dataUrl: url,
    fileType: ATTACHMENT_TYPES.AUDIO,
    extension: 'wav',
    transcribedText: call.value?.transcript || '',
  };
});

const handleJoinCall = async () => {
  if (!canJoinCall.value || isJoining.value) return;

  if (hasActiveCall.value && activeCall.value?.callSid !== callSid.value) {
    await endCall({
      conversationId: activeCall.value.conversationId,
      inboxId: activeCall.value.inboxId,
      callSid: activeCall.value.callSid,
    });
  }

  await joinCall({
    conversationId: conversationId.value,
    inboxId: inboxId.value,
    callSid: callSid.value,
  });
};

const canCallBack = computed(
  () =>
    isMissedInbound.value &&
    !!inboxId.value &&
    !!conversationId.value &&
    !hasActiveCall.value &&
    !callsStore.hasIncomingCall
);

const handleCallBack = async () => {
  if (!canCallBack.value || isInitiatingCall.value) return;
  try {
    if (isWhatsapp.value) {
      const response = await whatsappCallSession.initiateOutboundCall({
        conversationId: conversationId.value,
      });
      if (response?.status === VOICE_CALL_OUTBOUND_INIT_STATUS.LOCKED) return;
      // Permission template path returns no call id — show banner, no widget yet.
      if (!response?.id) {
        useAlert(
          response?.status ===
            VOICE_CALL_OUTBOUND_INIT_STATUS.PERMISSION_PENDING
            ? t('CONVERSATION.HEADER.WHATSAPP_CALL_PERMISSION_PENDING')
            : t('CONVERSATION.HEADER.WHATSAPP_CALL_PERMISSION_REQUESTED')
        );
        return;
      }
      callsStore.addCall({
        callSid: response.call_id,
        callId: response.id,
        conversationId: conversationId.value,
        inboxId: inboxId.value,
        callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
        provider: VOICE_CALL_PROVIDERS.WHATSAPP,
      });
      return;
    }

    if (isFreeswitch.value) {
      const conversation = store.getters.getConversationById?.(
        conversationId.value
      );
      const phone =
        sender.value?.phone_number ||
        conversation?.meta?.sender?.phone_number;
      if (!phone) {
        useAlert(
          t('CONTACT_PANEL.PHONE_NOT_FOUND') ||
            'Contact phone number required to place call'
        );
        return;
      }
      const contactName =
        sender.value?.name || conversation?.meta?.sender?.name || phone;
      const inboxes = store.getters['inboxes/getInboxes'] || [];
      const currentInbox = inboxes.find(i => i.id === inboxId.value);
      const callerId = currentInbox?.telephony_number || currentInbox?.phone_number || '';
      const callSid = await FreeswitchVoiceClient.makeCall(phone, {
        inboxId: inboxId.value,
        callerId,
        contactId: sender.value?.id || conversation?.meta?.sender?.id,
        conversationId: conversationId.value,
      });

      callsStore.addCall({
        callSid,
        callId: callSid,
        conversationId: conversationId.value,
        inboxId: inboxId.value,
        callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
        provider: VOICE_CALL_PROVIDERS.FREESWITCH,
        caller: { name: contactName, phone },
      });
      return;
    }

    const response = await store.dispatch('contacts/initiateCall', {
      contactId: sender.value?.id,
      inboxId: inboxId.value,
      conversationId: conversationId.value,
    });
    callsStore.addCall({
      callSid: response?.call_sid,
      conversationId: response?.conversation_id ?? conversationId.value,
      inboxId: inboxId.value,
      callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
    });
  } catch (error) {
    useAlert(error?.message || t('CONTACT_PANEL.CALL_FAILED'));
  }
};
</script>

<template>
  <BaseBubble class="!p-3 !max-w-md min-w-[240px]" hide-meta>
    <div class="flex flex-col gap-3 w-full">
      <!-- Header row: icon + title + duration/subtext -->
      <div class="flex gap-2.5 items-start">
        <div
          class="flex justify-center items-center rounded-xl size-11 shrink-0"
          :class="iconContainerClass"
        >
          <Icon class="size-4" :icon="iconName" />
        </div>
        <div class="flex flex-col flex-1 min-w-0 self-center">
          <span
            class="font-display text-sm font-medium leading-tight truncate tracking-tight"
          >
            {{ $t(labelKey) }}
          </span>
          <span
            v-if="subtext"
            class="text-sm leading-tight truncate tracking-tight opacity-75"
          >
            {{ subtext }}
          </span>
        </div>
      </div>

      <!-- Audio player (when there's a recording) -->
      <AudioChip
        v-if="recordingAttachment"
        :attachment="recordingAttachment"
        show-transcribed-text
      />

      <!-- Call back button (missed inbound) -->
      <NextButton
        v-if="canCallBack"
        type="button"
        :label="$t('CONVERSATION.VOICE_CALL.CALL_BACK')"
        icon="i-ph-phone-bold"
        teal
        class="!rounded-full"
        :disabled="isInitiatingCall"
        @click="handleCallBack"
      />

      <!-- Join call button (ringing inbound) -->
      <NextButton
        v-if="canJoinCall"
        type="button"
        :label="$t('CONVERSATION.VOICE_CALL.JOIN_CALL')"
        icon="i-ph-phone-bold"
        teal
        class="!rounded-full"
        :disabled="isJoining"
        @click="handleJoinCall"
      />
    </div>
  </BaseBubble>
</template>
